import React, { Component, Fragment } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import { FormattedMessageWrappedInSpan } from '../components/misc';
import includes from 'lodash.includes';

import AccountPhotoDownload from '../components/content/AccountPhotoDownload';
import DownloadDropdown from '../components/misc/DownloadDropdown';
import EventInfoCard from '../components/content/event/EventInfoCard';
import MediaList from '../components/content/lists/MediaList';
import PhotoBookInfoBanner from '../components/content/photo-book/PhotoBookInfoBanner';

import { includesPhotoBook } from '../utils/common';
import { ORIGINAL } from '../utils/variables';

class AccountEventPage extends Component {
  constructor(props) {
    super(props);

    const { availableZips, availableVideos, availableCertificates } = props.viewer.account.event;

    this.state = {
      showPhotoDownload: availableZips.length > 0,
      showVideoDownload: availableVideos.length > 0,
      hasCertificates: availableCertificates.length > 0,
      hasPhotoBook: includesPhotoBook(props.viewer.event.products),
    };

    this.handleCustomizePhotoBookClick = this.handleCustomizePhotoBookClick.bind(this);
  }

  handleCustomizePhotoBookClick() {
    const { history, match } = this.props;
    history.push(`/account/event/${match.params.id}/book`);
  }

  render() {
    const { intl, viewer } = this.props;
    const { account } = viewer;
    const { hasPhotoBook, hasCertificates, showPhotoDownload, showVideoDownload } = this.state;
    const originalsAreAvailable = includes(account.event.availableZips, ORIGINAL);

    return (
      <div>
        <div className="container-960 container-page">
          <div className="account-event-header">
            <div className="row account-event-info-card">
              <EventInfoCard eventInfo={viewer.event} eventBannerActAsLink={false} />
              {showPhotoDownload && (
                <AccountPhotoDownload
                  eventSgId={viewer.event.sgId}
                  availableZips={account.event.availableZips}
                />
              )}
            </div>
            {showVideoDownload && (
              <div className="row account-event-video-download">
                <div className="col-xs-16 col-sm-6 col-sm-offset-8">
                  <DownloadDropdown
                    eventSgId={viewer.event.sgId}
                    fullWidth={true}
                    downloadType="video"
                    values={account.event.availableVideos}
                  />
                </div>
              </div>
            )}
            {hasCertificates && !originalsAreAvailable && (
              <div className="row account-event-certificate-banner">
                <div className="col-xs-16 col-sm-6 col-sm-offset-8">
                  <div className="account-event-certificate-container">
                    <h2>
                      <FormattedMessageWrappedInSpan
                        id="accountEventPage.hasCertificates.noOriginals"
                        defaultMessage="Certificates will be offered for download as soon as the high-resolution images are available."
                      />
                    </h2>
                  </div>
                </div>
              </div>
            )}
            {hasCertificates && originalsAreAvailable && (
              <div className="row account-event-certificate-banner">
                <div className="col-xs-16 col-sm-6 col-sm-offset-8">
                  <div className="account-event-certificate-container">
                    <h2>
                      <FormattedMessageWrappedInSpan
                        id="accountEventPage.hasCertificates.withOriginals"
                        defaultMessage="Please click on a picture below to download it as a certificate."
                      />
                    </h2>
                  </div>
                </div>
              </div>
            )}
            {hasPhotoBook && (
              <div className="row">
                <div className="col-xs-16 col-sm-12 col-sm-offset-2">
                  <PhotoBookInfoBanner
                    eventId={viewer.event.sgId}
                    eventName={viewer.event.shortName}
                    onContinueClick={this.handleCustomizePhotoBookClick}
                  />
                </div>
              </div>
            )}
          </div>
          {account.event.participants.map(participant => (
            <Fragment key={participant.startnumber}>
              <div className="row">
                <div className="col-xs-16 col-sm-12 col-sm-offset-2">
                  <h1 className="separator">
                    {intl.formatMessage(translations.boughtPhotos, {
                      count: participant.media.mediaInfo.count,
                      startNumber: participant.startnumber,
                    })}
                  </h1>
                </div>
              </div>
              <MediaList
                media={participant.media.mediaList}
                offer={null}
                eventSgId={viewer.event.sgId}
                startNumbers={account.event.availableCertificates}
                listType="account"
                canRestartUpload={account.event.canRestart}
              />
            </Fragment>
          ))}
          {account.event.singleMedia.mediaInfo.count > 0 && (
            <Fragment>
              <div className="row">
                <div className="col-xs-16 col-sm-12 col-sm-offset-2">
                  <h1 className="separator">
                    {intl.formatMessage(translations.singleMedia, {
                      count: account.event.singleMedia.mediaInfo.count,
                    })}
                  </h1>
                </div>
              </div>
              <MediaList
                media={account.event.singleMedia.mediaList}
                offer={null}
                eventSgId={viewer.event.sgId}
                startNumbers={account.event.availableCertificates}
                listType="account"
                canRestartUpload={account.event.canRestart}
              />
            </Fragment>
          )}
          {account.event.bonusMedia.mediaInfo.count > 0 && (
            <Fragment>
              <div className="row">
                <div className="col-xs-16 col-sm-12 col-sm-offset-2">
                  <h1 className="separator">
                    {intl.formatMessage(translations.freePhotos, {
                      count: account.event.bonusMedia.mediaInfo.count,
                    })}
                  </h1>
                </div>
              </div>
              <MediaList
                media={account.event.bonusMedia.mediaList}
                eventSgId={viewer.event.sgId}
                offer={null}
                listType="account"
                canRestartUpload={account.event.canRestart}
              />
            </Fragment>
          )}
        </div>
      </div>
    );
  }
}

const translations = defineMessages({
  boughtPhotos: {
    id: 'accountEventPage.boughtPhotos',
    defaultMessage:
      'Listing {count, plural, one {# photo} other {# photos}} for start number {startNumber}',
  },
  singleMedia: {
    id: 'accountEventPage.singleMedia',
    defaultMessage:
      'Listing {count, plural, one {# photo} other {# photos}} you bought for this event',
  },
  freePhotos: {
    id: 'accountEventPage.freePhotos',
    defaultMessage:
      'Listing {count, plural, one {# free photo} other {# free photos}} for this event',
  },
  requestVideoButton: {
    id: 'accountEventPage.requestVideoButton',
    defaultMessage: 'Generate video for start number {startNumber}',
  },
  downloadVideoButton: {
    id: 'accountEventPage.downloadVideoButton',
    defaultMessage: 'Download video for start number {startNumber}',
  },
});

AccountEventPage.propTypes = {
  intl: PropTypes.object,
  viewer: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
};

export { AccountEventPage };

export default createFragmentContainer(injectIntl(AccountEventPage), {
  viewer: graphql`
    fragment AccountEventPage_viewer on Viewer {
      event(id: $eventId) {
        name
        products {
          type
        }
        sgId
        shortName
        ...EventInfoCard_eventInfo
      }
      account {
        event(byStartnumber: true, eventId: $eventId) {
          availableCertificates
          availableVideos
          availableZips
          bonusMedia {
            mediaInfo {
              count
            }
            mediaList(first: 1000) {
              edges {
                node {
                  id
                  sgId
                  variants {
                    format
                    name
                    url
                  }
                }
              }
              ...MediaList_media
            }
          }
          canRestart
          participants {
            media {
              mediaInfo {
                count
              }
              mediaList(first: 1000) {
                edges {
                  node {
                    id
                    sgId
                    variants {
                      format
                      name
                      url
                    }
                  }
                }
                ...MediaList_media
              }
            }
            startnumber
          }
          singleMedia {
            mediaInfo {
              count
            }
            mediaList(first: 1000) {
              edges {
                node {
                  id
                  sgId
                  variants {
                    format
                    name
                    url
                  }
                }
              }
              ...MediaList_media
            }
          }
        }
      }
    }
  `,
});
